import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import MessageVariablesPicker from "../MessageVariablesPicker";

const useStyles = makeStyles((theme) => ({
  caixa: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  previa: {
    whiteSpace: "pre-wrap",
    background: theme.palette.type === "dark" ? "#2b2b2b" : "#f5f5f5",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    fontSize: 13,
  },
}));

/** {{1}}, {{2}}... na ordem em que aparecem, sem repetir. */
export const extrairVariaveis = (texto) => {
  const achados = String(texto || "").match(/\{\{\s*\d+\s*\}\}/g) || [];
  const numeros = achados.map((m) => m.replace(/[^\d]/g, ""));

  return numeros.filter((n, i) => numeros.indexOf(n) === i);
};

const componentesDeTexto = (template) =>
  (template?.components || []).filter((c) => {
    const tipo = String(c.type || "").toLowerCase();
    if (tipo === "body" || tipo === "footer") return true;
    // header de mídia não tem texto para preencher
    return (
      tipo === "header" && String(c.format || "TEXT").toUpperCase() === "TEXT"
    );
  });

const textoDoCorpo = (template) => {
  const corpo = (template?.components || []).find(
    (c) => String(c.type || "").toLowerCase() === "body"
  );

  return corpo?.text || "";
};

const renderizar = (texto, valores) =>
  String(texto || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (inteiro, n) =>
    valores[n] ? valores[n] : inteiro
  );

/**
 * Seletor de template + campos das variáveis, para agendamento em conexão
 * WhatsApp Oficial. Fora da janela de 24h a Meta só aceita template, então o
 * texto livre não serve.
 *
 * Os valores das variáveis aceitam as variáveis do sistema ({{firstName}},
 * {{ms}}...): elas são resolvidas no BACKEND, no momento do disparo — é o que
 * faz a saudação sair certa num agendamento criado horas antes.
 */
const ScheduleTemplateFields = ({
  whatsappId,
  templateId,
  onTemplateChange,
  variables,
  onVariablesChange,
  onPreviewChange,
  disabled,
}) => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const ultimoCampo = useRef(null);

  // Guardado em ref para o efeito da prévia não depender da identidade da
  // função (o pai recria a cada render e isso viraria laço infinito).
  const avisarPrevia = useRef(onPreviewChange);
  avisarPrevia.current = onPreviewChange;

  useEffect(() => {
    if (!whatsappId) {
      setTemplates([]);
      return undefined;
    }

    let vivo = true;
    setCarregando(true);

    api
      .get("/quick-messages/list", {
        params: { isOficial: true, whatsappId },
      })
      .then(({ data }) => {
        if (!vivo) return;

        const lista = (Array.isArray(data) ? data : []).filter(
          (t) =>
            String(t.status || "").toUpperCase() === "APPROVED" &&
            // O backend hoje ignora o whatsappId no filtro, então filtramos
            // aqui. Template com whatsappId nulo é o sincronizado antes da
            // coluna existir — entra, senão o seletor fica vazio. Ver o SQL
            // de correção no README.
            (!t.whatsappId || Number(t.whatsappId) === Number(whatsappId))
        );

        setTemplates(lista);
      })
      .catch((err) => {
        if (!vivo) return;
        setTemplates([]);
        toastError(err);
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });

    return () => {
      vivo = false;
    };
  }, [whatsappId]);

  const selecionado = useMemo(
    () => templates.find((t) => Number(t.id) === Number(templateId)) || null,
    [templates, templateId]
  );

  const variaveis = useMemo(() => {
    const todas = componentesDeTexto(selecionado).flatMap((c) =>
      extrairVariaveis(c.text)
    );

    return todas.filter((n, i) => todas.indexOf(n) === i);
  }, [selecionado]);

  const previa = useMemo(
    () => renderizar(textoDoCorpo(selecionado), variables || {}),
    [selecionado, variables]
  );

  useEffect(() => {
    if (avisarPrevia.current) avisarPrevia.current(previa);
  }, [previa]);

  const alterarVariavel = useCallback(
    (numero, valor) => {
      onVariablesChange({ ...(variables || {}), [numero]: valor });
    },
    [onVariablesChange, variables]
  );

  const inserirVariavelDoSistema = useCallback(
    (valor) => {
      const alvo = ultimoCampo.current || variaveis[0];
      if (!alvo) return;

      const atual = (variables || {})[alvo] || "";
      alterarVariavel(alvo, `${atual}${valor}`);
    },
    [alterarVariavel, variables, variaveis]
  );

  return (
    <Paper variant="outlined" className={classes.caixa}>
      <Typography variant="subtitle2">
        {i18n.t("scheduleModal.template.title")}
      </Typography>

      <FormControl variant="outlined" margin="dense" fullWidth>
        <InputLabel id="schedule-template-label">
          {i18n.t("scheduleModal.template.select")}
        </InputLabel>
        <Select
          labelId="schedule-template-label"
          label={i18n.t("scheduleModal.template.select")}
          value={selecionado ? templateId : ""}
          onChange={(e) => onTemplateChange(e.target.value)}
          disabled={disabled || carregando}
        >
          {templates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {template.shortcode}
              {template.language ? ` (${template.language})` : ""}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {templates.length === 0 && !carregando
            ? i18n.t("scheduleModal.template.empty")
            : i18n.t("scheduleModal.template.help")}
        </FormHelperText>
      </FormControl>

      {variaveis.length > 0 && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 8 }}>
            {i18n.t("scheduleModal.template.variables")}
          </Typography>

          <Grid container spacing={1}>
            {variaveis.map((numero) => (
              <Grid item xs={12} md={6} key={numero}>
                <TextField
                  label={`${i18n.t("scheduleModal.template.variable")} {{${numero}}}`}
                  value={(variables || {})[numero] || ""}
                  onChange={(e) => alterarVariavel(numero, e.target.value)}
                  onFocus={() => {
                    ultimoCampo.current = numero;
                  }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  disabled={disabled}
                />
              </Grid>
            ))}
          </Grid>

          <MessageVariablesPicker
            disabled={disabled}
            showSchedulingVars={true}
            onClick={inserirVariavelDoSistema}
          />
        </>
      )}

      {selecionado && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 8 }}>
            {i18n.t("scheduleModal.template.preview")}
          </Typography>
          <div className={classes.previa}>{previa}</div>
        </>
      )}
    </Paper>
  );
};

export default ScheduleTemplateFields;
